import React, { useState } from 'react';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../../../config/apiRouting';

interface OnboardingData {
  posType: string;
  posCredentials: {
    clientId: string;
    clientSecret: string;
  };
  restaurantExternalId: string;
}

interface POSConnectionProps {
  data: OnboardingData;
  onUpdate: (data: Partial<OnboardingData>) => void;
  storeId: string;
}

type ConnectionStatus = 'idle' | 'testing' | 'success' | 'failure';

interface POSOption {
  id: string;
  name: string;
  icon: string;
  available: boolean;
  description: string;
}

const POS_OPTIONS: POSOption[] = [
  {
    id: 'toast',
    name: 'Toast',
    icon: 'fas fa-fire',
    available: true,
    description: 'Full integration with Toast POS for orders, menus, and reporting',
  },
  {
    id: 'clover',
    name: 'Clover',
    icon: 'fas fa-leaf',
    available: true,
    description: 'Clover POS integration for payments and order management',
  },
  {
    id: 'square',
    name: 'Square',
    icon: 'fas fa-square',
    available: true,
    description: 'Square POS integration for payments, inventory, and analytics',
  },
  {
    id: 'olo',
    name: 'OLO',
    icon: 'fas fa-mobile-alt',
    available: true,
    description: 'OLO digital ordering platform for online and mobile orders',
  },
  {
    id: 'revel',
    name: 'Revel',
    icon: 'fas fa-tablet-alt',
    available: true,
    description: 'Revel Systems iPad POS for restaurants and retail',
  },
  {
    id: 'brink',
    name: 'Brink',
    icon: 'fas fa-cash-register',
    available: true,
    description: 'Brink POS cloud-based point of sale for restaurants',
  },
  {
    id: 'paytronix',
    name: 'Paytronix',
    icon: 'fas fa-gift',
    available: true,
    description: 'Paytronix loyalty and guest engagement platform',
  },
  {
    id: 'none',
    name: 'No POS',
    icon: 'fas fa-ban',
    available: true,
    description: 'Continue without a POS connection. You can set one up later.',
  },
];

export const POSConnection: React.FC<POSConnectionProps> = ({ data, onUpdate, storeId }) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  const [connectionMessage, setConnectionMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handlePosTypeSelect = (posId: string) => {
    const option = POS_OPTIONS.find((o) => o.id === posId);
    if (!option || !option.available) return;

    onUpdate({ posType: posId });
    setConnectionStatus('idle');
    setConnectionMessage('');
    setError(null);
  };

  const handleCredentialChange = (field: string, value: string) => {
    onUpdate({
      posCredentials: {
        ...data.posCredentials,
        [field]: value,
      },
    });
    // Reset connection status when credentials change
    if (connectionStatus !== 'idle') {
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  };

  const handleExternalIdChange = (value: string) => {
    onUpdate({ restaurantExternalId: value });
    if (connectionStatus !== 'idle') {
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  };

  const handleTestConnection = async () => {
    if (!data.posCredentials.clientId || !data.posCredentials.clientSecret) {
      setError('Please enter both Client ID and Client Secret before testing.');
      return;
    }

    setConnectionStatus('testing');
    setConnectionMessage('Testing connection to Toast...');
    setError(null);

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posConnectionTest(storeId), {
        method: 'POST',
        body: JSON.stringify({
          posType: data.posType,
          credentials: {
            clientId: data.posCredentials.clientId,
            clientSecret: data.posCredentials.clientSecret,
          },
          restaurantExternalId: data.restaurantExternalId,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setConnectionStatus('success');
        setConnectionMessage(result.message || 'Connection successful. Your Toast POS is linked.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setConnectionStatus('failure');
        setConnectionMessage(
          errorData.message || 'Connection failed. Please verify your credentials and try again.'
        );
      }
    } catch (err) {
      setConnectionStatus('failure');
      setConnectionMessage(
        err instanceof Error
          ? err.message
          : 'Unable to reach the server. Please check your network and try again.'
      );
    }
  };

  return (
    <div>
      <h2>POS Connection</h2>
      <p className="wizard-step-description">
        Connect your Point of Sale system to enable order syncing, real-time analytics, and
        automated reporting. Select your POS provider below.
      </p>

      {error && (
        <div className="wizard-alert wizard-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {/* POS Type Selector */}
      <div className="pos-type-grid">
        {POS_OPTIONS.map((option) => (
          <div
            key={option.id}
            className={`pos-type-card ${data.posType === option.id ? 'selected' : ''} ${
              !option.available ? 'disabled' : ''
            }`}
            onClick={() => handlePosTypeSelect(option.id)}
          >
            {!option.available && <div className="pos-coming-soon-badge">Coming Soon</div>}
            <div className="pos-type-card-icon">
              <i className={option.icon}></i>
            </div>
            <div className="pos-type-card-name">{option.name}</div>
            <div className="pos-type-card-status">{option.description}</div>
          </div>
        ))}
      </div>

      {/* Toast Credential Form */}
      {data.posType === 'toast' && (
        <div className="pos-credential-form">
          <h3>Toast API Credentials</h3>
          <p className="wizard-step-description">
            Enter your Toast API credentials. These can be found in your Toast Partner Portal
            under API Configuration.
          </p>

          <div className="wizard-form-group">
            <label>
              Client ID <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.posCredentials.clientId}
              onChange={(e) => handleCredentialChange('clientId', e.target.value)}
              placeholder="Enter your Toast Client ID"
            />
          </div>

          <div className="wizard-form-group">
            <label>
              Client Secret <span className="required">*</span>
            </label>
            <input
              type="password"
              value={data.posCredentials.clientSecret}
              onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
              placeholder="Enter your Toast Client Secret"
            />
          </div>

          <div className="wizard-form-group">
            <label>Restaurant External ID</label>
            <input
              type="text"
              value={data.restaurantExternalId}
              onChange={(e) => handleExternalIdChange(e.target.value)}
              placeholder="Toast restaurant GUID (optional)"
            />
            <small>
              The unique identifier for your restaurant in the Toast system. If left blank, it
              will be auto-detected during connection.
            </small>
          </div>

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={
              connectionStatus === 'testing' ||
              !data.posCredentials.clientId ||
              !data.posCredentials.clientSecret
            }
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Clover Credential Form */}
      {data.posType === 'clover' && (
        <div className="pos-credential-form">
          <h3>Clover API Credentials</h3>
          <p className="wizard-step-description">
            Enter your Clover API credentials. These can be found in your Clover Developer Dashboard
            or Clover App Market settings.
          </p>

          <div className="wizard-form-group">
            <label>
              Merchant ID <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.restaurantExternalId}
              onChange={(e) => handleExternalIdChange(e.target.value)}
              placeholder="Enter your Clover Merchant ID"
            />
          </div>

          <div className="wizard-form-group">
            <label>
              API Token <span className="required">*</span>
            </label>
            <input
              type="password"
              value={data.posCredentials.clientSecret}
              onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
              placeholder="Enter your Clover API Token"
            />
            <small>
              The API token is generated from your Clover Developer Dashboard or when installing
              a Clover app.
            </small>
          </div>

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={
              connectionStatus === 'testing' ||
              !data.restaurantExternalId ||
              !data.posCredentials.clientSecret
            }
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Square Credential Form */}
      {data.posType === 'square' && (
        <div className="pos-credential-form">
          <h3>Square API Credentials</h3>
          <p className="wizard-step-description">
            Enter your Square API credentials. These can be found in your Square Developer Dashboard
            under Applications.
          </p>

          <div className="wizard-form-group">
            <label>
              Application ID <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.posCredentials.clientId}
              onChange={(e) => handleCredentialChange('clientId', e.target.value)}
              placeholder="Enter your Square Application ID"
            />
          </div>

          <div className="wizard-form-group">
            <label>
              Access Token <span className="required">*</span>
            </label>
            <input
              type="password"
              value={data.posCredentials.clientSecret}
              onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
              placeholder="Enter your Square Access Token"
            />
          </div>

          <div className="wizard-form-group">
            <label>Location ID</label>
            <input
              type="text"
              value={data.restaurantExternalId}
              onChange={(e) => handleExternalIdChange(e.target.value)}
              placeholder="Square Location ID (optional - auto-detected)"
            />
            <small>
              The unique identifier for your Square location. If left blank, it will be
              auto-detected during connection.
            </small>
          </div>

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={
              connectionStatus === 'testing' ||
              !data.posCredentials.clientId ||
              !data.posCredentials.clientSecret
            }
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* OLO Credential Form */}
      {data.posType === 'olo' && (
        <div className="pos-credential-form">
          <h3>OLO API Credentials</h3>
          <p className="wizard-step-description">
            Enter your OLO API credentials from your OLO Partner Portal.
          </p>

          <div className="wizard-form-group">
            <label>
              Client ID <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.posCredentials.clientId}
              onChange={(e) => handleCredentialChange('clientId', e.target.value)}
              placeholder="Enter your OLO Client ID"
            />
          </div>

          <div className="wizard-form-group">
            <label>
              Client Secret <span className="required">*</span>
            </label>
            <input
              type="password"
              value={data.posCredentials.clientSecret}
              onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
              placeholder="Enter your OLO Client Secret"
            />
          </div>

          <div className="wizard-form-group">
            <label>Brand ID</label>
            <input
              type="text"
              value={data.restaurantExternalId}
              onChange={(e) => handleExternalIdChange(e.target.value)}
              placeholder="OLO Brand ID"
            />
          </div>

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={
              connectionStatus === 'testing' ||
              !data.posCredentials.clientId ||
              !data.posCredentials.clientSecret
            }
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Generic Credential Form for Revel, Brink, Paytronix */}
      {(['revel', 'brink', 'paytronix'].includes(data.posType)) && (
        <div className="pos-credential-form">
          <h3>{POS_OPTIONS.find(o => o.id === data.posType)?.name} API Credentials</h3>
          <p className="wizard-step-description">
            Enter your API credentials for {POS_OPTIONS.find(o => o.id === data.posType)?.name}.
          </p>

          <div className="wizard-form-group">
            <label>
              API Key / Client ID <span className="required">*</span>
            </label>
            <input
              type="text"
              value={data.posCredentials.clientId}
              onChange={(e) => handleCredentialChange('clientId', e.target.value)}
              placeholder="Enter your API Key or Client ID"
            />
          </div>

          <div className="wizard-form-group">
            <label>
              API Secret / Client Secret <span className="required">*</span>
            </label>
            <input
              type="password"
              value={data.posCredentials.clientSecret}
              onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
              placeholder="Enter your API Secret or Client Secret"
            />
          </div>

          <div className="wizard-form-group">
            <label>External ID / Establishment ID</label>
            <input
              type="text"
              value={data.restaurantExternalId}
              onChange={(e) => handleExternalIdChange(e.target.value)}
              placeholder="External identifier (optional)"
            />
          </div>

          <button
            className="test-connection-btn"
            onClick={handleTestConnection}
            disabled={
              connectionStatus === 'testing' ||
              !data.posCredentials.clientId ||
              !data.posCredentials.clientSecret
            }
          >
            {connectionStatus === 'testing' ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Testing Connection...
              </>
            ) : (
              <>
                <i className="fas fa-plug"></i>
                Test Connection
              </>
            )}
          </button>

          {connectionStatus !== 'idle' && connectionStatus !== 'testing' && (
            <div className={`connection-status ${connectionStatus}`}>
              <i
                className={`fas ${
                  connectionStatus === 'success' ? 'fa-check-circle' : 'fa-times-circle'
                }`}
              ></i>
              <span>{connectionMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* No POS Selected */}
      {data.posType === 'none' && (
        <div className="wizard-alert wizard-alert-info">
          <i className="fas fa-info-circle"></i>
          <span>
            You can connect a POS system at any time from the store settings. Some features like
            order analytics and real-time reporting will be limited until a POS is connected.
          </span>
        </div>
      )}
    </div>
  );
};

export default POSConnection;
